import React from "react";
import { Moon, Utensils, Sparkles } from "lucide-react";

/**
 * LocalExperienceCard — Phase 14.9.15 · 4th intent.
 *
 * Renders nightlife + food joints + specialty dishes for a destination.
 * Three vertically-stacked panels on mobile, side-by-side on desktop
 * via a responsive grid. Each row that carries a `book_url` renders as
 * a click-through pill; rows without one stay static.
 *
 * Card schema (see /app/memory/CHAT_RESPONSE_RULES.md):
 *   { type: "local_experience", destination_name, region, country,
 *     nightlife: [{name, area, type, vibe, price_tier, book_url?}],
 *     food_joints: [{name, cuisine, area, must_try_dishes, price_tier, book_url?}],
 *     specialties: [{dish_name, description, where_to_try, book_url?}],
 *     source: "cache" | "llm" }
 */

const PIN = "#EC4899";   // pink dot — matches the backend `color_dot`.
const RULE = "#E5E5E5";
const INK = "#0A0A0A";
const MUTE = "#737373";

function Row({ href, children, testid }) {
    if (!href) {
        return (
            <div className="py-1.5" data-testid={testid}>
                {children}
            </div>
        );
    }
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-md px-1 py-1.5 transition hover:bg-[#F5F5F5]"
            data-testid={testid}
        >
            {children}
        </a>
    );
}

function Panel({ icon: Icon, title, subtitle, children, testid }) {
    return (
        <div
            className="flex flex-col rounded-xl border border-[#E5E5E5] bg-white p-4"
            data-testid={testid}
        >
            <div className="mb-2 flex items-center gap-2">
                <Icon className="h-4 w-4" strokeWidth={2} color={INK} />
                <div>
                    <div className="text-[13px] font-semibold text-[#0A0A0A]">
                        {title}
                    </div>
                    {subtitle && (
                        <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                            {subtitle}
                        </div>
                    )}
                </div>
            </div>
            <div className="divide-y divide-[#F5F5F5]">{children}</div>
        </div>
    );
}

export default function LocalExperienceCard({ card }) {
    if (!card) return null;
    const nightlife = Array.isArray(card.nightlife) ? card.nightlife : [];
    const food = Array.isArray(card.food_joints) ? card.food_joints : [];
    const specialties = Array.isArray(card.specialties) ? card.specialties : [];
    const dest = card.destination_name || "";
    const region = [card.region, card.country].filter(Boolean).join(", ");

    return (
        <div
            className="relative rounded-2xl border border-[#E5E5E5] bg-white p-5 my-3"
            data-testid="local-experience-card"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: PIN }}
            />
            <div className="mb-4">
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                    Local Experience
                </div>
                <div className="text-[18px] font-semibold tracking-tight text-[#0A0A0A]">
                    {dest}
                </div>
                {region && (
                    <div className="text-[12px] text-[#737373]">{region}</div>
                )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {nightlife.length > 0 && (
                    <Panel
                        icon={Moon}
                        title="Nightlife"
                        subtitle={`${nightlife.length} picks`}
                        testid="local-exp-nightlife"
                    >
                        {nightlife.map((row, i) => (
                            <Row
                                key={`nl-${i}`}
                                href={row.book_url}
                                testid={`nightlife-row-${i}`}
                            >
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[13px] font-medium text-[#0A0A0A]">
                                        {row.name}
                                    </span>
                                    <span className="text-[10px] font-semibold tabular-nums text-[#A3A3A3]">
                                        {row.price_tier}
                                    </span>
                                </div>
                                <div className="text-[11px]" style={{ color: MUTE }}>
                                    {[row.area, row.type, row.vibe]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </div>
                            </Row>
                        ))}
                    </Panel>
                )}

                {food.length > 0 && (
                    <Panel
                        icon={Utensils}
                        title="Food joints"
                        subtitle={`${food.length} picks`}
                        testid="local-exp-food"
                    >
                        {food.map((row, i) => (
                            <Row
                                key={`fd-${i}`}
                                href={row.book_url}
                                testid={`food-row-${i}`}
                            >
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[13px] font-medium text-[#0A0A0A]">
                                        {row.name}
                                    </span>
                                    <span className="text-[10px] font-semibold tabular-nums text-[#A3A3A3]">
                                        {row.price_tier}
                                    </span>
                                </div>
                                <div className="text-[11px]" style={{ color: MUTE }}>
                                    {[row.cuisine, row.area].filter(Boolean).join(" · ")}
                                </div>
                                {row.must_try_dishes && row.must_try_dishes.length > 0 && (
                                    <div className="mt-0.5 text-[11px] italic text-[#525252]">
                                        Try: {row.must_try_dishes.slice(0, 3).join(", ")}
                                    </div>
                                )}
                            </Row>
                        ))}
                    </Panel>
                )}

                {specialties.length > 0 && (
                    <Panel
                        icon={Sparkles}
                        title="Local specialties"
                        subtitle={`${specialties.length} dishes`}
                        testid="local-exp-specialties"
                    >
                        {specialties.map((row, i) => (
                            <Row
                                key={`sp-${i}`}
                                href={row.book_url}
                                testid={`specialty-row-${i}`}
                            >
                                <div className="text-[13px] font-medium text-[#0A0A0A]">
                                    {row.dish_name}
                                </div>
                                {row.description && (
                                    <div className="text-[11px]" style={{ color: MUTE }}>
                                        {row.description}
                                    </div>
                                )}
                                {row.where_to_try && row.where_to_try.length > 0 && (
                                    <div className="mt-0.5 text-[11px] italic text-[#525252]">
                                        @ {row.where_to_try.slice(0, 2).join(", ")}
                                    </div>
                                )}
                            </Row>
                        ))}
                    </Panel>
                )}
            </div>

            {card.source === "cache" && (
                <div
                    className="mt-3 text-right text-[10px] uppercase tracking-wider text-[#A3A3A3]"
                    data-testid="local-exp-cache-badge"
                >
                    cached · refreshed monthly
                </div>
            )}
        </div>
    );
}
